// Supabase 클라이언트 전용 fetch 래퍼 — 응답이 오지 않는 요청을 강제로 끊는다.
//
// 배경 (2026-07-25 "오프라인 로딩 고착" 사고):
//   @supabase/auth-js(v2.105.4 실측)의 토큰 refresh 요청에는 타임아웃이 전혀 없다
//   (`lib/fetch.js`의 `fetcher(url, requestParams)` 호출에 signal 미부여).
//   앱을 오래 켜두면 access token 만료 임박 상태가 되고, `_autoRefreshTokenTick`이
//   30초마다 auth 락을 잡은 채 refresh를 시도한다. 이때 오프라인/VPN 후킹으로 연결이
//   hang하면 락을 쥔 채 영구히 매달린다.
//   그 사이 화면에서 들어온 `getSession()`은 `_acquireLock`의 "이미 락 보유 중" 분기로
//   빠져 `lockAcquireTimeout`(기본 5초)이 **적용되지 않는 큐**에서 무한 대기한다
//   (GoTrueClient.js:2256~2271) → apiFetch가 fetch에 도달조차 못 해 로딩 고착.
//
//   → 여기서 refresh 요청 자체에 타임아웃을 주어 좀비 락이 생기지 않게 한다(근본 차단).
//     supabase-js는 `global.fetch`를 auth·postgrest·storage에 모두 전달하므로
//     (index.cjs:381→547 / :388,398,402) 이 한 곳으로 3계층이 함께 보호된다.

import { recordNetworkSuccess, recordTransportFailure } from "./netHealth";

/** 일반 요청 타임아웃. auth refresh·postgrest 조회 모두 이 안에 끝나야 정상. */
export const SUPABASE_FETCH_TIMEOUT_MS = 15_000;

/**
 * 비멱등 쓰기(POST/PATCH/PUT/DELETE)용 타임아웃 — N-26 (2026-07-30).
 *
 * ⚠ 위 15초는 **auth 좀비 락을 끊기 위한** 값인데, supabase-js가 같은 fetch를
 *   postgrest에도 넘기는 바람에 글·댓글·목표 생성 같은 쓰기까지 끊었다.
 *   서버가 INSERT를 commit한 뒤 응답 본문이 15초 안에 도달하지 못하면
 *   **앱은 실패로 표시하고 사용자는 다시 등록한다 → 같은 글/댓글이 중복 생성**된다.
 *   (삭제·수정은 반영됐는데 "실패"로 보이는 반대 불일치도 같은 원인이다.)
 *
 * 🔑 목적이 다르니 값도 나눈다 — 좀비 락 차단은 auth·조회에만 필요하고,
 *   쓰기는 "끊어서 얻는 이득"보다 "중복 생성"이 훨씬 비싸다.
 *   완전히 없애지는 않는다(무한 대기는 여전히 화면을 잡아먹는다).
 */
export const SUPABASE_WRITE_TIMEOUT_MS = 60_000;

/** 이 요청에 적용할 타임아웃(ms). auth·조회는 짧게, 비멱등 쓰기는 길게. */
export function timeoutForRequest(url: string, method: string | undefined): number {
  // auth refresh가 매달리면 락이 좀비가 된다 — 이 경로는 반드시 짧게 끊는다(사고 원인).
  if (url.includes("/auth/v1/")) return SUPABASE_FETCH_TIMEOUT_MS;
  const m = String(method || "GET").toUpperCase();
  // 조회는 끊어도 재시도가 안전하다(부작용 없음).
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return SUPABASE_FETCH_TIMEOUT_MS;
  return SUPABASE_WRITE_TIMEOUT_MS;
}

/**
 * 업로드성 요청인지 판정 — 사진 업로드는 느린 회선에서 15초를 넘길 수 있어
 * 타임아웃을 걸지 않는다(끊으면 정상 업로드를 죽인다).
 */
function isUploadBody(body: unknown): boolean {
  if (body == null) return false;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  if (typeof Blob !== "undefined" && body instanceof Blob) return true;
  if (body instanceof ArrayBuffer) return true;
  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream) return true;
  return ArrayBuffer.isView(body as ArrayBufferView | null);
}

/**
 * 타임아웃용 signal 생성. `AbortSignal.timeout`이 없는 구형 WebView도 커버해야 하므로
 * (이 방어가 곧 고착 차단이라 폴백 없이 두면 옛 기기에서 무방비) AbortController로 폴백한다.
 * @returns signal과 정리 함수. cleanup은 요청 종료 후 반드시 호출(타이머 누수 방지).
 */
function createTimeoutSignal(ms: number): { signal: AbortSignal; cleanup: () => void } {
  if (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal) {
    return { signal: AbortSignal.timeout(ms), cleanup: () => {} };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, cleanup: () => clearTimeout(timer) };
}

/**
 * 타임아웃이 붙은 fetch를 만든다.
 * - 호출자가 이미 signal을 넘긴 경우엔 그대로 존중(취소 책임이 호출자에게 있음 — apiFetch와 동일 규칙)
 * - 업로드성 body는 타임아웃 제외
 * - 전송계층 성공/실패를 netHealth에 반영 → supabase 직접 호출(`from`/`storage`)도 VPN 힌트에 기여
 *
 * @param baseFetch 실제 fetch (테스트에서 주입)
 * @param timeoutMs 타임아웃(ms)
 */
export function createTimeoutFetch(
  baseFetch: typeof fetch,
  timeoutMs: number = SUPABASE_FETCH_TIMEOUT_MS,
): typeof fetch {
  return async function timeoutFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const callerSignal = init?.signal;
    const skipTimeout = callerSignal != null || isUploadBody(init?.body);
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;

    if (skipTimeout) {
      try {
        const res = await baseFetch(input, init);
        recordNetworkSuccess();
        return res;
      } catch (e) {
        // 호출자가 직접 abort한 경우는 네트워크 문제가 아니므로 제외
        if (callerSignal?.aborted !== true) recordTransportFailure(url);
        throw e;
      }
    }

    // N-26: 요청 성격별로 상한을 고른다. 인자로 받은 timeoutMs가 기본값과 다르면
    //   호출자가 명시적으로 지정한 것이므로 그 값을 존중한다(테스트 주입 등).
    const method = input instanceof Request ? input.method : init?.method;
    const effectiveTimeout = timeoutMs === SUPABASE_FETCH_TIMEOUT_MS
      ? timeoutForRequest(url, method)
      : timeoutMs;
    const { signal, cleanup } = createTimeoutSignal(effectiveTimeout);
    try {
      const res = await baseFetch(input, { ...init, signal });
      // 응답을 받았다 = 전송 경로 정상(4xx/5xx여도 서버가 답한 것)
      recordNetworkSuccess();
      return res;
    } catch (e) {
      recordTransportFailure(url);
      throw e;
    } finally {
      cleanup();
    }
  } as typeof fetch;
}
